import {FC, memo, useEffect, useState} from 'react';
import {api, hasValue, IAttachmentData, useT} from '@kcast-fe/core';
import {Loader, SmartImage, Svg} from 'ui-kit';
import {Box, Flex} from '@chakra-ui/react';
import {getOrFetchFile} from 'core';
import FileSaver from 'file-saver';

interface IProps {
  isMyMsg: boolean;
  attachment: IAttachmentData;
  isMsgHasText: boolean;
  widthPx: number;
}

const RenderImageFC: FC<IProps> = ({isMyMsg, attachment, isMsgHasText, widthPx}) => {
  const attachMutation = api.documents.useAttachContextDocumentMutation();

  const [isLoading, setIsLoading] = useState(true);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [imageUrl, setImageUrl] = useState('');

  const {t} = useT();

  useEffect(() => {
    if (!hasValue(attachment.id) || !hasValue(attachment.name)) {
      setIsLoading(false);
      return;
    }

    let isActive = true;
    getOrFetchFile(attachment)
      .then((resp) => {
        if (isActive) {
          setBlob(resp);
        }
      })
      .catch(() => console.error('[File] Download error'))
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachment.id]);

  useEffect(() => {
    if (!blob) {
      return;
    }
    const url = URL.createObjectURL(blob);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  const handleAttach = () => {
    if (attachment.id) {
      attachMutation.mutate(attachment.id);
    }
  };

  const handleDownload = () => {
    if (blob) {
      FileSaver.saveAs(blob, attachment.name);
    }
  };

  return (
    <Flex gap={'10px'} flexDirection={'column'}>
      <Box overflow={'hidden'} borderRadius={'12px'}>
        {hasValue(imageUrl) && <SmartImage src={imageUrl} height={widthPx} />}
      </Box>

      <Flex alignItems={'center'} p={isMsgHasText ? '0 12px' : '0'}>
        {isLoading ? (
          <Box>{t('labels.imageRendering')}</Box>
        ) : (
          <Flex w={'100%'} justify={'space-between'}>
            <Flex gap={'4px'} _hover={{cursor: 'pointer', opacity: 0.8}} onClick={handleDownload}>
              <Svg name={'Download'} />
              <Box>{isMyMsg ? t('labels.imageUploaded') : t('labels.imageCreated')}</Box>
            </Flex>
            <Flex
              gap={'4px'}
              _hover={{cursor: 'pointer', opacity: 0.8}}
              onClick={() => {
                if (!attachMutation.isPending) {
                  handleAttach();
                }
              }}
            >
              {attachMutation.isPending ? <Loader size={'sm'} /> : <Svg name={'Plus'} />}
              <Box>{t('labels.addToContext')}</Box>
            </Flex>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export const RenderImage = memo(RenderImageFC);
